import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonBase,
  TextField,
  Typography,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Box,
} from "@material-ui/core";
import { InsertDriveFile, NoteAdd } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { listWhatsapps } from "../../services/whatsapp.api";
import { FLOW_CHANNELS, FLOW_CHANNEL_META, flowFitsConnection } from "../../constants/flowChannels";

/**
 * Nuevo flujo — asistente de 3 pasos (doc 0438 Parte VII, FB8), con el mismo patrón que el de importar:
 * una pregunta por paso → ¿qué canal? → ¿cómo empiezas? → nombre y activación (con resumen).
 *
 * El canal se elige aquí y no cambia (0371 WF0). Decide qué nodos ofrece el editor, con qué
 * conexiones puede ir el flujo y cómo lo verá el cliente: cada canal tiene sus propios flujos.
 */
const useStyles = makeStyles((theme) => ({
  pasos: { display: "flex", gap: 16, marginBottom: 20, color: theme.palette.text.secondary, fontSize: 13, flexWrap: "wrap" },
  pasoActivo: { color: theme.palette.text.primary, fontWeight: 600 },
  tarjetas: { display: "grid", gridTemplateColumns: "1fr", gap: 12 },
  tarjetas2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" } },
  tarjeta: {
    display: "flex", alignItems: "center", textAlign: "left", gap: 14,
    width: "100%", padding: 16, borderRadius: 10, border: `1px solid ${theme.palette.divider}`,
    transition: "border-color .15s, background .15s",
    "&:hover": { borderColor: theme.palette.primary.main, background: theme.palette.action.hover },
  },
  tarjetaVertical: { flexDirection: "column", alignItems: "flex-start", gap: 6, padding: 20 },
  logo: { width: 40, height: 40, flexShrink: 0 },
  icono: { fontSize: 30, color: theme.palette.primary.main },
  ayuda: { color: theme.palette.text.secondary, fontSize: 13, lineHeight: 1.45 },
  resumen: { padding: 14, borderRadius: 10, background: theme.palette.action.hover, marginTop: 16, fontSize: 13, lineHeight: 1.6 },
  lista: { maxHeight: 160, overflow: "auto", border: `1px solid ${theme.palette.divider}`, borderRadius: 8, padding: "2px 8px", marginTop: 8 },
}));

export default function NewFlowModal({ open, onClose, onConfirm, onImport }) {
  const classes = useStyles();
  const [paso, setPaso] = useState(1);
  const [canal, setCanal] = useState(null);
  const [name, setName] = useState("");
  const [activacion, setActivacion] = useState("despues");
  const [palabra, setPalabra] = useState("");
  const [conexiones, setConexiones] = useState([]);
  const [elegidas, setElegidas] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPaso(1);
    setCanal(null);
    setName("");
    setActivacion("despues");
    setPalabra("");
    setElegidas([]);
  }, [open]);

  useEffect(() => {
    if (!open || paso !== 3 || !canal) return undefined;
    let vivo = true;
    listWhatsapps()
      .then(({ data }) => {
        const lista = Array.isArray(data) ? data : data?.whatsapps || [];
        if (!vivo) return;
        const delCanal = lista.filter((w) => flowFitsConnection(canal, w.provider));
        setConexiones(delCanal);
        setElegidas(delCanal.filter((w) => w.status === "CONNECTED").map((w) => w.id));
      })
      .catch(() => vivo && setConexiones([]));
    return () => {
      vivo = false;
    };
  }, [open, paso, canal]);

  const meta = canal ? FLOW_CHANNEL_META[canal] : null;
  const conPalabra = activacion === "palabra";
  const listo = name.trim() && (!conPalabra || (palabra.trim() && elegidas.length > 0));
  const nombresElegidas = useMemo(
    () => conexiones.filter((w) => elegidas.includes(w.id)).map((w) => w.name).join(", "),
    [conexiones, elegidas]
  );

  const cerrar = () => {
    if (!saving) onClose();
  };

  const crear = async () => {
    if (!listo) return;
    try {
      setSaving(true);
      await onConfirm({
        name: name.trim(),
        provider: canal,
        ...(conPalabra ? { keyword: palabra.trim(), whatsappIds: elegidas } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  const Pasos = () => (
    <div className={classes.pasos}>
      {["Canal", "Cómo empezar", "Nombre y activación"].map((t, i) => (
        <span key={t} className={paso === i + 1 ? classes.pasoActivo : undefined}>{i + 1}. {t}</span>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onClose={cerrar} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
          {meta && <img src={meta.icon} alt="" style={{ width: 26, height: 26 }} />}
          Nuevo flujo{meta ? ` · ${meta.label}` : ""}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Pasos />

        {paso === 1 && (
          <>
            <Typography variant="body2" gutterBottom>¿Por qué canal hablará este flujo con tus clientes?</Typography>
            <div className={classes.tarjetas}>
              {FLOW_CHANNELS.map((c) => (
                <ButtonBase key={c} className={classes.tarjeta} onClick={() => { setCanal(c); setPaso(2); }}>
                  <img src={FLOW_CHANNEL_META[c].icon} alt="" className={classes.logo} />
                  <span>
                    <Typography variant="subtitle1"><b>{FLOW_CHANNEL_META[c].long}</b></Typography>
                    <span className={classes.ayuda}>{FLOW_CHANNEL_META[c].description}</span>
                  </span>
                </ButtonBase>
              ))}
            </div>
            <Typography className={classes.ayuda} style={{ marginTop: 12, fontStyle: "italic" }}>
              Cada canal tiene sus propios flujos y el canal no se cambia después de crearlo.
            </Typography>
          </>
        )}

        {paso === 2 && (
          <>
            <Typography variant="body2" gutterBottom>¿Cómo quieres empezar?</Typography>
            <div className={classes.tarjetas2}>
              <ButtonBase className={`${classes.tarjeta} ${classes.tarjetaVertical}`} onClick={() => setPaso(3)}>
                <NoteAdd className={classes.icono} />
                <Typography variant="subtitle1"><b>En blanco</b></Typography>
                <span className={classes.ayuda}>Abres el editor con el flujo vacío y lo construyes nodo a nodo.</span>
              </ButtonBase>
              <ButtonBase
                className={`${classes.tarjeta} ${classes.tarjetaVertical}`}
                onClick={() => onImport && onImport(canal)}
                disabled={!onImport}
              >
                <InsertDriveFile className={classes.icono} />
                <Typography variant="subtitle1"><b>Importar</b></Typography>
                <span className={classes.ayuda}>Desde Typebot o desde un archivo de FlowBuilder. Los flujos nacen en el canal {meta?.label}.</span>
              </ButtonBase>
            </div>
          </>
        )}

        {paso === 3 && (
          <>
            <TextField
              autoFocus
              fullWidth
              size="small"
              variant="outlined"
              label="Nombre del flujo"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Typography variant="body2" style={{ marginTop: 18, marginBottom: 4 }}>¿Cuándo se activa?</Typography>
            <RadioGroup value={activacion} onChange={(e) => setActivacion(e.target.value)}>
              <FormControlLabel
                value="despues"
                control={<Radio color="primary" size="small" />}
                label={<span className={classes.ayuda}>Lo asigno después: a una conexión, a un departamento o desde otro flujo</span>}
              />
              <FormControlLabel
                value="palabra"
                control={<Radio color="primary" size="small" />}
                label={<span className={classes.ayuda}>Cuando el cliente escriba una palabra clave</span>}
              />
            </RadioGroup>
            {conPalabra && (
              <Box ml={4}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Palabra clave exacta"
                  placeholder="Ej.: hola, menu, cita"
                  value={palabra}
                  onChange={(e) => setPalabra(e.target.value)}
                />
                <Typography className={classes.ayuda} style={{ marginTop: 10 }}>
                  En estas conexiones de {meta?.label}:
                </Typography>
                {conexiones.length === 0 ? (
                  <Typography variant="caption" color="error">No hay conexiones de {meta?.long}. Crea una primero o elige «Lo asigno después».</Typography>
                ) : (
                  <div className={classes.lista}>
                    {conexiones.map((w) => (
                      <FormControlLabel
                        key={w.id}
                        style={{ display: "flex" }}
                        control={
                          <Checkbox
                            size="small"
                            color="primary"
                            checked={elegidas.includes(w.id)}
                            onChange={(e) => setElegidas((v) => (e.target.checked ? [...v, w.id] : v.filter((x) => x !== w.id)))}
                          />
                        }
                        label={<span className={classes.ayuda}>{w.name}{w.status !== "CONNECTED" ? " (desconectada)" : ""}</span>}
                      />
                    ))}
                  </div>
                )}
              </Box>
            )}
            <div className={classes.resumen}>
              <b>Se va a crear</b>
              <br />• Flujo «{name.trim() || "…"}» en el canal <b>{meta?.long}</b>, vacío, y se abre el editor.
              <br />
              {conPalabra
                ? <>• Se activa cuando escriban «{palabra.trim() || "…"}» en: {nombresElegidas || "…"}.</>
                : <>• Sin activar todavía: asígnalo desde la conexión, el departamento o un salto de otro flujo.</>}
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {paso > 1 && <Button onClick={() => setPaso(paso - 1)} disabled={saving}>Atrás</Button>}
        <Button onClick={cerrar} disabled={saving}>Cancelar</Button>
        {paso === 3 && (
          <Button
            variant="contained"
            color="primary"
            onClick={crear}
            disabled={saving || !listo}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? "Creando…" : "Crear y abrir el editor"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
